import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { filter, map, Observable, switchMap, take } from 'rxjs';
import { CoreService } from '../services/core.service';
import { UserService } from '../services/user.service';
import {
  ADMIN_SIDE_NAVIGATION_ITEMS,
  getCarrierFleetOwnerSideNavigationItems,
  CARRIER_OWNER_OPERATOR_SIDE_NAVIGATION_ITEMS,
  CUSTOMER_SIDE_NAVIGATION_ITEMS,
  SideNavigationItem,
} from '../constants/navigation/navigation-items.constants';
import { getRequiredPermissions } from '../constants/navigation/nav-item.utils';
import { CarrierOwnerType, PlatformType, ProfileWebModel } from '@data-access';

const NAV_ITEMS_BY_CABINET: Record<PlatformType, SideNavigationItem[]> = {
  WEB_ADMIN: ADMIN_SIDE_NAVIGATION_ITEMS,
  WEB_CARRIER: [],
  WEB_CUSTOMER: CUSTOMER_SIDE_NAVIGATION_ITEMS,
  WEB_LOGIN: [],
  ANDROID: [],
  IOS: [],
};

/**
 * Every route that belongs to *some* carrier type. Used by
 * {@link permissionCarrierGuard} to tell apart "this route is for the other
 * carrier type" (must be blocked) from "this route isn't a carrier nav route at
 * all" (genuinely open).
 */
export function getAllCarrierNavItems(isDev: boolean): SideNavigationItem[] {
  return [
    ...CARRIER_OWNER_OPERATOR_SIDE_NAVIGATION_ITEMS,
    ...getCarrierFleetOwnerSideNavigationItems(isDev),
  ];
}

/**
 * Guards a dashboard child route against the permissions declared for it in the
 * nav constants (single source of truth, matched by route `path` === nav `routerLink`).
 *
 * On denial it redirects to the first route the user is actually allowed to open,
 * so closed routes never render an empty screen. Routes without declared
 * permissions are treated as open.
 *
 * The check is async on purpose: Angular runs guards *before* resolvers, so on a
 * hard reload permissions aren't loaded yet — we wait for `ensureProfileLoaded()`
 * before deciding, instead of wrongly bouncing the user off a valid route.
 *
 * Usage: `canActivate: [permissionGuard('WEB_CARRIER')]`
 */
export function permissionGuard(cabinet: PlatformType): CanActivateFn {
  return (route): Observable<boolean | UrlTree> => {
    const core = inject(CoreService);
    const router = inject(Router);
    const userService = inject(UserService);

    const path = route.routeConfig?.path ?? '';
    const required = getRequiredPermissions(NAV_ITEMS_BY_CABINET[cabinet], path);

    return userService.getProfile().pipe(
      map(() => {
        if (!required || core.checkPermission(cabinet, required)) {
          return true;
        }

        const target = core.getFirstAllowedRoute(cabinet);

        // No accessible route at all means the user has no cabinet access — let the
        // dashboard resolver run and redirect to auth/registration instead of
        // bouncing to an empty `/dashboard` (which would loop back into this guard).
        if (!target) {
          return true;
        }

        return router.createUrlTree(['dashboard', target]);
      }),
    );
  };
}

/**
 * Carrier-specific variant of {@link permissionGuard}.
 *
 * `permissionGuard('WEB_CARRIER')` matches a route against the *combined* carrier
 * nav items, which can't tell apart permissions declared only for one carrier
 * type. This guard first reads `carrierOwnerType` from the core state and then
 * matches the route against the nav items for that exact type
 * (`CARRIER_OWNER_OPERATOR_SIDE_NAVIGATION_ITEMS` vs
 * `CARRIER_FLEET_OWNER_SIDE_NAVIGATION_ITEMS`).
 *
 * The profile is taken from `UserService.selectUserProfile$()`. On the first
 * visit the session is empty (`undefined`), so we *must* wait before deciding —
 * otherwise `carrierOwnerType` reads as `undefined`, falls into the FLEET_OWNER
 * branch, and lets an OWNER_OPERATOR onto fleet-only routes. `ensureProfileLoaded()`
 * fetches and pushes the profile into the store, then we wait for the first
 * defined profile (`filter` + `take(1)`) before resolving the guard.
 *
 * A route that isn't declared for the user's carrier type but *is* declared for
 * the other carrier type is treated as forbidden (redirect), not open. Routes not
 * declared for any carrier type are treated as open.
 *
 * On denial it redirects to the first route the user is allowed to open.
 *
 * Usage: `canActivate: [permissionCarrierGuard()]`
 */
export function permissionCarrierGuard(): CanActivateFn {
  return (route): Observable<boolean | UrlTree> => {
    const core = inject(CoreService);
    const router = inject(Router);
    const userService = inject(UserService);

    const path = route.routeConfig?.path ?? '';

    return userService.getProfile().pipe(
      switchMap(() => userService.selectUserProfile$()),
      filter((profile): profile is ProfileWebModel => !!profile),
      take(1),
      map((profile) => {
        const carrierOwnerType = profile.access?.carriers?.[0]?.carrierOwnerType;
        const navItems =
          carrierOwnerType === CarrierOwnerType.OwnerOperator
            ? CARRIER_OWNER_OPERATOR_SIDE_NAVIGATION_ITEMS
            : getCarrierFleetOwnerSideNavigationItems(core.isDevModeEnabled());

        const required = getRequiredPermissions(navItems, path);

        // Gated for this carrier type → must hold the permission. Not gated but
        // declared for the *other* carrier type → forbidden. Not a carrier route
        // at all → open.
        const isCarrierRoute = getAllCarrierNavItems(core.isDevModeEnabled()).some(
          (item) => item.routerLink === path,
        );
        const allowed = required ? core.checkPermission('WEB_CARRIER', required) : !isCarrierRoute;

        if (allowed) {
          return true;
        }

        const target = core.getFirstAllowedRoute('WEB_CARRIER');

        // No accessible route at all means the user has no carrier access — let the
        // dashboard resolver run and redirect to auth/registration instead of
        // bouncing to an empty `/dashboard` (which would loop back into this guard).
        if (!target) {
          return true;
        }

        return router.createUrlTree(['dashboard', target]);
      }),
    );
  };
}

/**
 * Redirects the empty/wildcard dashboard path to the first route the user is
 * allowed to open. Implemented as a guard (not a function `redirectTo`) because
 * redirects are resolved during route recognition — even earlier than guards —
 * when permissions aren't loaded yet on a hard reload.
 *
 * Attach to a component-less landing route:
 * `{ path: '', pathMatch: 'full', canActivate: [firstAllowedRedirectGuard('WEB_CARRIER')], children: [] }`
 */
export function firstAllowedRedirectGuard(cabinet: PlatformType): CanActivateFn {
  return (route): Observable<boolean | UrlTree> => {
    const core = inject(CoreService);
    const router = inject(Router);
    const userService = inject(UserService);

    return userService.getProfile().pipe(
      map(() => {
        const target = core.getFirstAllowedRoute(cabinet);

        // No accessible route → defer to the dashboard resolver's access/registration
        // gating rather than looping back into this same empty route.
        // The query parameters travel with the redirect: the dashboard reads them only once it
        // has landed (`showCongratulations` after a carrier registration).
        return target
          ? router.createUrlTree(['dashboard', target], { queryParams: route.queryParams })
          : true;
      }),
    );
  };
}
